import React, {createContext, useState, useEffect, useMemo, useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import queryString from 'query-string';
import {parseQueryParams, haversineDistance} from '../helpers/base';
import useIsMobile from '../hooks/useIsMobile';

export const AppContext = createContext();

/****************************************************************************************
 * Common helpers
 */

const ITEMS_PER_PAGE = 12;

const propertyMap = {
    accoType: {property: 'accommodationTypeId', type: 'int'},
    awards: {property: 'ratingAwardId', type: 'int'},
    topics: {property: 'features', type: 'array'},
    specializations: {property: 'specializations', type: 'array'},
    specialOffers: {property: 'specialOffers', type: 'array'},
    region: {property: 'regions', type: 'array'},
    state: {property: 'stateId', type: 'int'},
    accoFeatures: {property: 'features', type: 'array'},
    animals: {property: 'animals', type: 'array'},
    locations: {property: 'locations', type: 'array'},
    mealPlans: {property: 'mealPlans', type: 'array'},
    roomTypes: {property: 'roomTypes', type: 'array'},
    experienceWorlds: {property: 'experienceWorlds', type: 'array'},
    accessible: {property: 'accessibleFacilities', type: 'int'},
    petsAllowed: {property: 'petsAllowed', type: 'int'},
};

const isEmptyFilterValue = (value) =>
    (Array.isArray(value) && value.length === 0) ||
    (!Array.isArray(value) && (value === null || value === undefined));

const toArray = (v) => (Array.isArray(v) ? v : [v]);

const buildPreprocessedFilters = (selectedFilters) => {
    const preprocessed = [];

    for (const [filterType, filterValue] of Object.entries(selectedFilters || {})) {
        const mapEntry = propertyMap[filterType];
        if (!mapEntry || isEmptyFilterValue(filterValue))
            continue;

        preprocessed.push({
            filterType,
            filterValue,
            property: mapEntry.property,
            type: mapEntry.type,
        });
    }

    return preprocessed;
};

const matchesFilter = (result, filter) => {
    const {filterValue, property, type} = filter;
    const value = result[property];

    const values = toArray(filterValue);

    if (type === 'int') {
        return values.includes(value);
    }

    if (type === 'array') {
        if (!Array.isArray(value) || !value.length) return false;
        // overlap selected vs result[property]
        const valueSet = new Set(value);
        return values.some(v => valueSet.has(v));
    }

    console.warn(`Unsupported property type: ${type}`);
    return false;
};

const applySorting = (sortBy, filteredResults) => {

    const sortedResults = [...filteredResults];

    const makeAwardComparator = (orderArray, isAscending = false) => {
        const orderIndex = new Map(orderArray.map((id, index) => [id, index]));

        return (a, b) => {
            const aScore = a.ratingScoreId;
            const bScore = b.ratingScoreId;

            if (aScore == null && bScore == null) return 0;
            if (aScore == null) return 1;
            if (bScore == null) return -1;

            const aIndex = orderIndex.has(aScore) ? orderIndex.get(aScore) : -1;
            const bIndex = orderIndex.has(bScore) ? orderIndex.get(bScore) : -1;

            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            // fallback to numeric comparison
            return isAscending ? aScore - bScore : bScore - aScore;
        };
    };

    switch (sortBy) {
        case 'price-asc':
            sortedResults.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
            break;
        case 'price-desc':
            sortedResults.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
            break;
        case 'distance':
            sortedResults.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
            break;
        case 'award-asc':
            // aufsteigend: almhütte (9), premiumhütte (8), codex(10)
            sortedResults.sort(makeAwardComparator([9, 8, 10], true));
            break;
        case 'award-desc':
            // absteigend: premiumhütte (8), almhütte (9), codex(10)
            sortedResults.sort(makeAwardComparator([8, 9, 10], false));
            break;
        case 'rating-desc':
            sortedResults.sort((a, b) => (b.trustYouScore ?? -Infinity) - (a.trustYouScore ?? -Infinity));
            break;
        default:
            break;
    }

    return sortedResults;
};

const filterResults = (selectedFilters, subFilters, results) => {
    const preprocessedFilters = buildPreprocessedFilters(selectedFilters, subFilters);

    // 1) apply all non-distance/non-price filters
    let filtered = results.filter(result =>
        preprocessedFilters.every(filter => matchesFilter(result, filter))
    );

    // when prices available and discount filter active, filter by actual discount
    if (selectedFilters.discount === 1 && results.some(r => r.price !== null)) {
        filtered = filtered.filter(r => r.discountPercent !== null);
    }

    // 2) distance filter
    const hasCoords = selectedFilters.lat && selectedFilters.lng;
    if (hasCoords) {
        const {lat, lng, distance} = selectedFilters;
        const selectedDistance = distance ? Number(distance) : 20;

        filtered = filtered.filter(result => {
            if (!result.latitude || !result.longitude) return false;

            const d = haversineDistance(lat, lng, result.latitude, result.longitude);
            if (d <= selectedDistance) {
                result.distance = d;
                return true;
            }
            return false;
        });
    }

    // snapshot before price filter
    const filteredResultsWithoutPriceFilter = filtered.slice();

    // 3) price filter
    if (selectedFilters.price) {
        const [minPrice, maxPrice] = selectedFilters.price.split('-').map(Number);
        filtered = filtered.filter(result =>
            result.price >= minPrice && result.price <= maxPrice
        );
    }

    // 4) sorting
    if (selectedFilters.sort) {
        filtered = applySorting(selectedFilters.sort, filtered);
    }

    return {
        filteredResults: filtered,
        filteredResultsWithoutPriceFilter,
    };
};

const getSubFilters = (filters, accommodationType = null) => {
    if (!accommodationType || !filters?.accommodationType?.options) return {};

    const accommodation = filters.accommodationType.options.find(
        item => item.id === accommodationType
    );
    if (!accommodation?.filters) return {};

    return accommodation.filters.reduce((acc, filter, index) => {
        const uniqueOptions = [
            ...new Map((filter.options || []).map(opt => [opt.id, opt])).values()
        ];
        acc[index] = {...filter, options: uniqueOptions};
        return acc;
    }, {});
};

const collectFilterOptions = (data = {}, existingFilters = []) => {
    const allFilters = [...existingFilters];
    const uniqueIds = new Set(allFilters.map(f => f.id));

    const pushIfNew = (id, item) => {
        if (uniqueIds.has(id)) return;
        uniqueIds.add(id);
        allFilters.push(item);
    };

    const addOptions = (options = []) => {
        for (const {id, label, isGroup, items} of options) {
            pushIfNew(id, {
                id,
                label,
                isGroup,
                ...(isGroup && {items}),
            });
        }
    };

    const accommodationType = data.accommodationType;
    if (accommodationType?.options) {
        for (const {id, label, filters: typeFilters = []} of accommodationType.options) {
            pushIfNew(id, {id, label});
            typeFilters.forEach(f => addOptions(f.options));
        }
    }

    if (data.global) {
        Object.values(data.global).forEach(globalFilter => addOptions(globalFilter.options));
    }

    const current = data.current;
    if (current && !Array.isArray(current)) {
        Object.values(current).forEach(addOptions);
    }

    return allFilters;
};

/****************************************************************************************
 * AppProvider Component
 */

export const AppProvider = ({children, endpoint}) => {

    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile(992);
    const currentFilters = parseQueryParams(location);
    const locale = document.documentElement.lang?.substring(0, 2) ?? 'de';

    const [isInitializing, setIsInitializing] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState([]);
    const [results, setResults] = useState([]);
    const [isFilterOverlayVisible, setIsFilterOverlayVisible] = useState(false);
    const [isSortOverlayVisible, setIsSortOverlayVisible] = useState(false);
    const [isDistanceOverlayVisible, setIsDistanceOverlayVisible] = useState(false);
    const [isBookingBoxOverlayVisible, setIsBookingBoxOverlayVisible] = useState(false);
    const [hasResultsWithPrices, setHasResultsWithPrices] = useState(false);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [currentMapPoi, setCurrentMapPoi] = useState(null);
    const [filteredResults, setFilteredResults] = useState([]);
    const [filteredResultsWithoutPriceFilter, setFilteredResultsWithoutPriceFilter] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState(currentFilters);
    const [subFilters, setSubFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(currentFilters.page ? Number(currentFilters.page) : 1);
    const [endpointParams, setEndpointParams] = useState(currentFilters);
    const [allFilters, setAllFilters] = useState([]);
    const [poolEnquiryData, setPoolEnquiryData] = useState([]);
    const [resultsCache, setResultsCache] = useState({});
    const [teaserAlternativeStyle, setTeaserAlternativeStyle] = useState(false);

    const applyFilters = useCallback((selectedFiltersParam, subFiltersParam, resultsParam) => {
        const {
            filteredResults: newFilteredResults,
            filteredResultsWithoutPriceFilter: newFilteredResultsWithoutPriceFilter,
        } = filterResults(selectedFiltersParam, subFiltersParam, resultsParam);

        setFilteredResultsWithoutPriceFilter(newFilteredResultsWithoutPriceFilter);
        setFilteredResults(newFilteredResults);
    }, []);

    const handlePageChange = (page) => {
        setPage(page);
    };

    const setPage = (page) => {
        setCurrentPage(page);
        const queryParams = queryString.stringify({...selectedFilters, page}, {arrayFormat: 'bracket'});
        navigate(`?${queryParams}`, {replace: true});
        window.requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'instant'}));
    };

    const handleAccommodationTypeChange = (accommodationType) => {

        const newFilters = {
            ...selectedFilters
        };

        if (accommodationType) {
            newFilters.accoType = accommodationType;
        } else {
            delete newFilters.accoType;
        }

        const newSubFilters = getSubFilters(filters, accommodationType);

        // current filters coming from request (used to merge in options missing in filters)
        const currentFiltersLocal = filters.current && !Array.isArray(filters.current) ? filters.current : {};

        // collect all global option IDs
        const globalOptionIds = new Set(
            (filters.global || []).flatMap(globalGroup =>
                (globalGroup.options || []).map(opt => opt.id)
            )
        );

        for (const [filterType, optionIds] of Object.entries(newFilters)) {
            if (filterType === 'accoType')
                continue;

            // only clean up array-based filters, not q, price, etc.
            if (!Array.isArray(optionIds))
                continue;

            // region and state are preserved too
            if (filterType === 'region' || filterType === 'state')
                continue;

            // build valid subfilter options for this filterType under the new accoType
            const options = [...(newSubFilters[filterType]?.options || [])];
            const currentOptions = currentFiltersLocal[filterType] || [];

            // merge current options in case they are missing in normal filters
            currentOptions.forEach(option => {
                if (!options.some(o => o.id === option.id)) {
                    options.push(option);
                }
            });

            const validSubfilterIds = new Set(options.map(option => option.id));

            const filteredIds = optionIds.filter(optionId => {
                // if this id belongs to a global filter, always keep it
                if (globalOptionIds.has(optionId)) {
                    return true;
                }
                // otherwise, only keep if still part of the subfilters
                return validSubfilterIds.has(optionId);
            });

            if (filteredIds.length > 0) {
                newFilters[filterType] = filteredIds;
            } else {
                // Nothing valid left -> remove that filterType altogether
                delete newFilters[filterType];
            }
        }

        setSelectedFilters(newFilters);
        setSubFilters(newSubFilters);
    };


    const handleFilterGroupChange = (items, isSelected) => {
        items.forEach(item => {
            handleFilterChange(item.type, item.id, isSelected);
        });
    };

    const handleFilterChange = (filterType, filterValue, isSelected) => {
        setSelectedFilters(prev => {
            const next = {...prev};
            const current = next[filterType];

            if (Array.isArray(current)) {
                next[filterType] = isSelected
                    ? (current.includes(filterValue) ? current : [...current, filterValue])
                    : current.filter(v => v !== filterValue);

                if (!next[filterType].length) {
                    delete next[filterType];
                }
            } else {
                if (isSelected) {
                    next[filterType] = [filterValue];
                } else {
                    delete next[filterType];
                }
            }

            // Clear geo-related filters when removing q/region
            if (['q', 'region'].includes(filterType) && !isSelected) {
                delete next.lat;
                delete next.lng;
                delete next.q;
                delete next.distance;
                if (next.sort === 'distance') {
                    delete next.sort;
                }
            }

            return next;
        });

        // Keep endpointParams in sync only when needed
        if (hasResultsWithPrices && ['mealPlans', 'roomTypes'].includes(filterType)) {
            setEndpointParams(prev => {
                const current = prev[filterType] || [];
                let nextValues;

                if (isSelected) {
                    nextValues = current.includes(filterValue)
                        ? current
                        : [...current, filterValue];
                } else {
                    nextValues = current.filter(v => v !== filterValue);
                }

                if (!nextValues.length) {
                    const {[filterType]: _, ...rest} = prev;
                    return rest;
                }

                return {
                    ...prev,
                    [filterType]: nextValues,
                };
            });
        }
    };

    const handleSimpleFilterChange = (filterType, isSelected) => {
        setSelectedFilters(prev => {
            const filtersLocal = {...prev};
            if (isSelected) {
                filtersLocal[filterType] = 1;
            } else {
                delete filtersLocal[filterType];
            }
            return filtersLocal;
        });
    };

    const handlePriceRangeChange = (min, max) => {
        setSelectedFilters(prev => ({...prev, price: min + '-' + max}));
    };

    const handleSortChange = (value) => {
        setSelectedFilters(prev => ({
            ...prev,
            sort: value
        }));
    };

    const handleSearchQueryChange = (option) => {
        const {value, additionalInputs} = option;
        const {state, region, lat, lng, accommodation} = additionalInputs;

        if (accommodation) {
            const result = results.find(r => r.accommodationId === accommodation);
            if (result?.detailPageUrl) {
                window.location = result.detailPageUrl;
                return;
            }
        }

        setSelectedFilters(prev => {
            const next = (state || region) ? {...prev} : {...prev, q: value};

            if (lat && lng) {
                next.lat = lat;
                next.lng = lng;
                next.sort = 'distance';
            } else {
                delete next.lat;
                delete next.lng;
                if (next.sort === 'distance') delete next.sort;
            }

            if (state) {
                const currentStates = Array.isArray(next.state) ? next.state : (next.state != null ? [next.state] : []);
                if (!currentStates.includes(state)) {
                    next.state = [...currentStates, state];
                }
            }

            if (region) {
                const currentRegions = Array.isArray(next.region) ? next.region : [];
                if (!currentRegions.includes(region)) {
                    next.region = [...currentRegions, region];
                }
            }

            if (Array.isArray(next.region) && !next.region.length) {
                delete next.region;
            }

            return next;
        });

        if (region) {
            setAllFilters(prev => {
                const exists = prev.some(f => f.id === region);
                if (exists) return prev;
                return [...prev, {id: region, label: value}];
            });
        }
    };

    const handleClearFilters = () => {
        setSelectedFilters({});
    };

    const handleSubmitSearch = (selectedDates, rooms) => {

        const dates = {};

        if (selectedDates.length === 2) {
            dates.checkin = selectedDates[0].toLocaleDateString('en-CA');
            dates.checkout = selectedDates[1].toLocaleDateString('en-CA');
        } else {
            dates.checkin = undefined;
            dates.checkout = undefined;
        }

        const occupancy = {};

        rooms.forEach((group, index) => {
            occupancy[`adults-${index}`] = group.adults;
            occupancy[`childs-${index}`] = group.children;

            group.childAges.forEach((age, childIndex) => {
                occupancy[`childAge${index}-${childIndex + 1}`] = age;
            });
        });

        setSelectedFilters(prev => {

            const filtersLocal = Object.keys(prev).reduce((acc, key) => {
                if (!key.startsWith('adults-') && !key.startsWith('childs-') && !key.startsWith('childAge')) {
                    acc[key] = prev[key];
                }
                return acc;
            }, {});

            return {
                ...filtersLocal,
                ...occupancy,
                ...dates
            };
        });

        setEndpointParams(prev => ({
            ...prev,
            ...occupancy,
            ...dates
        }));

        setIsBookingBoxOverlayVisible(false);
    };

    const handleToggleMap = () => {
        setSelectedFilters(prev => ({
            ...prev,
            showMap: Number(!showMap)
        }));
        setShowMap(prev => !prev);
        setCurrentMapPoi(null);
    };

    const handleDistanceChange = (distance) => {
        setSelectedFilters(prev => ({
            ...prev,
            distance: Number(distance)
        }));
    };

    useEffect(() => {
        const seedLifetime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        const setNewSeed = () => {
            const newSeed = Math.floor((Math.random() + Math.random() * Date.now() % 1000) * 5000) + 1;
            try {
                window.localStorage.setItem('sortingSeed', newSeed.toString());
                window.localStorage.setItem('sortingSeedDate', Date.now().toString());
            } catch (e) {
                console.error('Failed to access localStorage:', e);
            }
        };

        const retrieveSeedData = () => {
            try {
                const seed = window.localStorage.getItem('sortingSeed');
                const date = window.localStorage.getItem('sortingSeedDate');
                return {seed, date};
            } catch (e) {
                console.error('Failed to access localStorage:', e);
                return {seed: null, date: null};
            }
        };

        const {seed, date} = retrieveSeedData();
        const now = Date.now();

        const seedNumber = parseInt(seed, 10);
        const dateNumber = parseInt(date, 10);

        // If the seed is missing, date is missing, or the seed has expired, set a new one
        if (
            isNaN(seedNumber) ||
            isNaN(dateNumber) ||
            (now - dateNumber > seedLifetime)
        ) {
            setNewSeed();
        }
    }, []);

    useEffect(() => {
        try {
            let sessionData = window.sessionStorage.getItem('accommodations');
            let data = sessionData !== null ? JSON.parse(sessionData) : [];
            setPoolEnquiryData(data);
        } catch (err) {
            // console.warn(`pool-enquiry: sessionStorage accommodations getItem failed!`);
        }
    }, []);

    useEffect(() => {
        setAllFilters(prevFilters => collectFilterOptions(filters, prevFilters));
    }, [filters]);

    useEffect(() => {
        if (!results?.length) return;
        const seen = new Set();
        const stateEntries = [];
        for (const r of results) {
            if (r.stateId && r.state && !seen.has(r.stateId)) {
                seen.add(r.stateId);
                stateEntries.push({id: r.stateId, label: r.state, isGroup: false});
            }
        }
        if (!stateEntries.length) return;
        setAllFilters(prev => {
            const existingIds = new Set(prev.map(f => f.id));
            const newEntries = stateEntries.filter(e => !existingIds.has(e.id));
            return newEntries.length ? [...prev, ...newEntries] : prev;
        });
    }, [results]);

    useEffect(() => {

        const existingParams = queryString.parseUrl(endpoint, {
            arrayFormat: 'bracket'
        }).query;

        const queryParams = queryString.stringify({...existingParams, ...endpointParams}, {arrayFormat: 'bracket'});
        const finalEndpoint = `${queryString.parseUrl(endpoint).url}?${queryParams}`;

        const fetchData = () => {
            const cacheKey = queryParams;

            if (resultsCache[cacheKey]) {
                return Promise.resolve(resultsCache[cacheKey]);
            }

            setIsLoading(true);
            return fetch(finalEndpoint, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-Sorting-Seed': localStorage.getItem('sortingSeed')
                }
            })
                .then(response => response.json())
                .then(data => {
                    setResultsCache(prev => ({
                        ...prev,
                        [cacheKey]: data
                    }));
                    return data;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        };

        const handleResponse = (data) => {
            const {filters: filtersData, results: resultsData, userLoggedIn, filterIdsRequestUrl, settings} = data;
            setFilters(filtersData);
            setResults(resultsData);
            setIsUserLoggedIn(userLoggedIn);
            setTeaserAlternativeStyle(settings?.teaserAlternativeStyle ?? false);
            setSubFilters(getSubFilters(filtersData, selectedFilters.accoType));
            window._config.filterIdsRequestUrl = filterIdsRequestUrl;
            window._config.filterIdsRequestUrlSingle = filterIdsRequestUrl;
            setTimeout(() => {
                setIsInitializing(false);
                setIsLoading(false);
            }, 0);
        };

        fetchData()
            .then(handleResponse)
            .catch(error => {
                console.error('Error fetching data:', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, endpointParams]);

    useEffect(() => {

        applyFilters(selectedFilters, subFilters, results);

        if (!isInitializing && currentPage > 1) {
            selectedFilters.page = 1;
            setPage(1);
        }

        const args = {...selectedFilters};

        if (selectedFilters.checkin && selectedFilters.checkout && selectedFilters['adults-0']) {
            setHasResultsWithPrices(true);
        } else {
            setHasResultsWithPrices(false);
            if (selectedFilters.discount) {
                setSelectedFilters(prev => {
                    const next = {...prev};
                    delete next.discount;
                    return next;
                });
            }
        }

        if (selectedFilters.showMap) {
            setShowMap(true);
            document.body.classList.add('is-fullscreen');
        } else {
            document.body.classList.remove('is-fullscreen');
        }

        const queryParams = queryString.stringify(args, {arrayFormat: 'bracket'});
        navigate(`?${queryParams}`, {replace: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters, results, applyFilters, navigate]);

    // push filter changes to dataLayer
    useEffect(() => {

        if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
            return;
        }

        const excludedKeys = ['page'];
        const filteredFiltersLocal = {...selectedFilters};
        Object.keys(filteredFiltersLocal).forEach(key => {
            if (excludedKeys.includes(key) || filteredFiltersLocal[key] === undefined) {
                delete filteredFiltersLocal[key];
            }
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'accommodationFiltered',
            'accommodationFilterObject': filteredFiltersLocal
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters]);

    useEffect(() => {
        if (isFilterOverlayVisible || isBookingBoxOverlayVisible || isSortOverlayVisible) {
            document.body.style.position = 'fixed';
        } else {
            document.body.style.position = '';
        }
    }, [isFilterOverlayVisible, isBookingBoxOverlayVisible, isSortOverlayVisible]);


    return (
        <AppContext.Provider value={useMemo(() => ({
            isInitializing,
            isLoading,
            locale,
            filters,
            subFilters,
            results,
            filteredResults,
            filteredResultsWithoutPriceFilter,
            selectedFilters,
            setSelectedFilters,
            allFilters,
            currentPage,
            itemsPerPage: ITEMS_PER_PAGE,
            isSortOverlayVisible,
            setIsSortOverlayVisible,
            isFilterOverlayVisible,
            setIsFilterOverlayVisible,
            isBookingBoxOverlayVisible,
            setIsBookingBoxOverlayVisible,
            isDistanceOverlayVisible,
            setIsDistanceOverlayVisible,
            hasResultsWithPrices,
            showMap,
            setShowMap,
            currentMapPoi,
            setCurrentMapPoi,
            isMobile,
            isUserLoggedIn,
            poolEnquiryData,
            teaserAlternativeStyle,
            handlePageChange,
            handleAccommodationTypeChange,
            handleFilterChange,
            handleFilterGroupChange,
            handleSimpleFilterChange,
            handlePriceRangeChange,
            handleSortChange,
            handleToggleMap,
            handleDistanceChange,
            handleSearchQueryChange,
            handleClearFilters,
            handleSubmitSearch
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }), [
            isInitializing,
            isLoading,
            filteredResults,
            subFilters,
            showMap,
            currentMapPoi,
            isMobile,
            currentPage,
            isSortOverlayVisible,
            isFilterOverlayVisible,
            isBookingBoxOverlayVisible,
            isDistanceOverlayVisible
        ])}>
            {children}
        </AppContext.Provider>
    );
};
